// import components
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import { ArrowNavigation, VirtualizedAutocomplete } from 'components/shared';
import {
  checkboxWidth,
  iconWidth,
  optionPadding
} from 'components/shared/Autocomplete/MuiAutocompleteOption';
import PlanGroupsDialog from 'components/shared/PlanGroups/PlanGroupsDialog';

// import hooks
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, usePlanUnitsOptions, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { usePrevious } from 'utils/reactHooks';
import {
  useFilterOptions,
  useGetOptionLabel,
  useRenderOption
} from './planUnitSelectorHooks';
import { getDefaultInterval, isValidPlanUnit } from './util';
import { useSessionStorage } from 'usehooks-ts';

// import utils
import classNames from 'classnames';
import { isEqual } from 'date-fns';
import { setUserPreference } from 'redux/actionCreators';
import { getPlanunitCommaIds } from 'components/utils';
import { getMinDate } from 'utils/date';

// import types
import { PlanGroup, PlanUnit, PlanUnitMix } from 'components/types';
import {
  StyledCustomTextField,
  StyledPlanUnitSelectorBox,
  StyledToolbarDivider
} from './PlanUnitSelector.styles';
import { PlanUnitSelectorProps } from './PlanUnitSelector.types';
import { Icon } from '@sdb/planning-ui';

export default function PlanUnitSelector<T extends boolean, U extends boolean>(
  props: PlanUnitSelectorProps<T, U>
) {
  const {
    className,
    defaultValue,
    onChange: fieldOnChange,
    doNotPreselectOnlyOption,
    firstOptionIsDefault,
    interval: intervalProp,
    tempOptions,
    useEmployeePlanunits = false,
    usePublicPlanUnits,
    fullWidth,
    hideNavigation,
    hideGroups,
    doNotPersist,
    isSingleSelect,
    disabled,
    filterByType,
    error,
    helperText,
    onClear,
    variant = 'standard',
    size = 'small',
    showChips = false,
    setPlanUnits,
    selectedValues,
    isToolbar = false,
    isVertical = false
  } = props;

  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const preferences = useSelector((state) => state.preferences);

  const shouldSaveCostCenterSelection =
    preferences[
      (!props.doNotPersist && props.shouldSaveCCPreferenceKey) ||
        'shouldSaveCostCenterSelection'
    ] ?? true;
  const lastUsedCostCenterSelection =
    preferences[
      (!props.doNotPersist && props.lastUsedCCPreferenceKey) ||
        'lastUsedCostCenterSelection'
    ] || [];
  const { hasKidsPlan } = useSelector((state) => state.grants);
  const interval = useMemo(
    () => intervalProp || getDefaultInterval(),
    [intervalProp]
  );
  const compareInterval = useMemo(
    () =>
      isEqual(interval.startDate, getMinDate())
        ? getDefaultInterval()
        : interval,
    [interval]
  );

  const options = usePlanUnitsOptions({
    showGroups: !hideGroups,
    useEmployeePlanunits,
    usePublicPlanUnits,
    interval,
    filterByType
  });

  const [hasUserSelection, setHasUserSelection] = useState(false);
  const [planUnitSearchCriteria, setPlanUnitSearchCriteria] =
    useSessionStorage<string>('planUnitSearchCriteria', '');
  const [inputValue, setInputValue] = useState(planUnitSearchCriteria);

  const [selectedOptionsIds, setSelectedOptionsIds] = useState<
    (string | number)[]
  >(
    tempOptions
      ? tempOptions.map((o) => o.id)
      : !doNotPersist && shouldSaveCostCenterSelection
      ? lastUsedCostCenterSelection
      : []
  );

  const getSelectedOptions = (): (PlanUnit | PlanGroup)[] => {
    if (
      options.length === 1 &&
      !('costCenters' in options[0]) &&
      !doNotPreselectOnlyOption
    ) {
      return [options[0]];
    }

    if (selectedOptionsIds.length) {
      return options.filter((o) => selectedOptionsIds.includes(o.uniqId));
    }

    if (hasUserSelection) {
      return []; // User has cleared the selection
    }

    let uniqIds: (string | number)[] = [];

    if (defaultValue && !(!doNotPersist && shouldSaveCostCenterSelection)) {
      uniqIds = defaultValue.map((o) => (o.uniqId ? o.uniqId : o.id));
    } else if (firstOptionIsDefault && !hasUserSelection) {
      uniqIds = options
        .filter((o) => !('costCenters' in o))
        .slice(0, 1)
        .map((o) => o.uniqId);
    }

    const selectedOptions = uniqIds
      .map((id) => options.filter(({ uniqId }) => uniqId === id)[0])
      .filter((v) => v);

    return selectedOptions;
  };

  const selectedOptions = getSelectedOptions();

  const getOptionLabel = useGetOptionLabel();
  const filterOptions = useFilterOptions();
  const renderOption = useRenderOption({ compareInterval });
  const renderMultiOption = useRenderOption({ isMulti: true, compareInterval });
  const [showPlanGroupsDialog, setShowPlanGroupsDialog] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const isMulti = selectedOptions.length > 1 || isOpen;
  const extraWidth =
    (isMulti ? checkboxWidth : 0) +
    (hasKidsPlan ? iconWidth : 0) +
    optionPadding * 2;

  const saveSelection = (value: (string | number)[]) => {
    dispatch(
      setUserPreference({
        key: props.lastUsedCCPreferenceKey || 'lastUsedCostCenterSelection',
        value
      })
    );
  };

  const onChange = (e, value) => {
    const selectionList: PlanUnitMix[] = Array.isArray(value)
      ? value.filter((v) => v)
      : value
      ? [value]
      : [];
    const ids = selectionList.map((o) => o.uniqId);
    setSelectedOptionsIds(ids);
    if (!doNotPersist && shouldSaveCostCenterSelection) {
      saveSelection(ids);
    }
    setHasUserSelection(true);
    fieldOnChange && fieldOnChange(selectionList);
  };

  const renderInput = useCallback(
    (params) => {
      const selectedOption = selectedOptions[0];
      const isGroup = selectedOption && 'costCenters' in selectedOption;

      if (
        selectedOption &&
        ('costCenters' in selectedOption ||
          (hasKidsPlan && selectedOption.isKids))
      ) {
        params.InputProps.startAdornment = (
          <InputAdornment position="start">
            <Icon
              type={isGroup ? 'folder' : 'children'}
              fontSize="small"
              color="inherit"
            />
          </InputAdornment>
        );
      }

      if (!isSingleSelect && !disabled) {
        params.InputProps.className += ' input-box';
        params.InputProps.endAdornment = (
          <div>
            <IconButton
              size="small"
              className="add-more"
              title={t('addMore')}
              onClick={() => {
                setOpen(true);
              }}
            >
              <Icon type="plus" fontSize="small" />
            </IconButton>
            {params.InputProps.endAdornment.props.children}
          </div>
        );
      }

      if (
        selectedOption &&
        !isGroup &&
        !isValidPlanUnit(selectedOption.validity, compareInterval)
      ) {
        params.inputProps.value = `${selectedOption.name} (${t(
          'invalid'
        ).toLowerCase()})`;
        params.InputProps.className += ' invalid';
      }

      return (
        <StyledCustomTextField
          label={t('planUnits')}
          error={error}
          variant={variant}
          hiddenLabel={isToolbar}
          helperText={helperText}
          isToolbar={isToolbar}
          size="small"
          {...params}
        />
      );
    },
    [hasKidsPlan, selectedOptions, compareInterval, t]
  );

  const renderTags = (options: PlanUnitMix[]) =>
    options.map((option) => {
      const isInvalid =
        !('costCenters' in option) &&
        !isValidPlanUnit(option.validity, compareInterval);
      return (
        <Chip
          key={option.uniqId}
          label={
            isInvalid
              ? `${option.name} (${t('invalid').toLowerCase()})`
              : option.name
          }
          variant="outlined"
          className={classNames({ invalid: isInvalid })}
          onDelete={() =>
            onChange(
              null,
              selectedOptions.filter((o) => o.uniqId !== option.uniqId)
            )
          }
        />
      );
    });
  const onArrowNavigate = (direction, currentIndex) => {
    let nextOption: PlanUnitMix;
    if (direction === 'prev') {
      nextOption = options[currentIndex - 1];
    } else {
      nextOption = selectedOptions[0] ? options[currentIndex + 1] : options[0];
      if ('costCenters' in nextOption) {
        const startIndex = selectedOptions[0] ? currentIndex + 1 : 0; // skip plangroups
        const planUnit = options.find(
          (o, i) => i > startIndex && !('costCenters' in o)
        );
        if (planUnit) {
          nextOption = planUnit;
        }
      }
    }
    onChange(null, [nextOption]);
  };

  const prevSelectedOptions = usePrevious(selectedOptions);
  useEffect(() => {
    const existingIds =
      prevSelectedOptions && getPlanunitCommaIds(prevSelectedOptions);
    const newIds = getPlanunitCommaIds(selectedOptions);
    if ((existingIds || newIds) && newIds !== existingIds) {
      props.onSelected(selectedOptions as PlanUnit[]);
    }
  }, [selectedOptions, prevSelectedOptions]);

  const prevShouldSaveCostCenterSelection = usePrevious(
    shouldSaveCostCenterSelection
  );
  useEffect(() => {
    if (
      !doNotPersist &&
      prevShouldSaveCostCenterSelection !== undefined &&
      prevShouldSaveCostCenterSelection !== shouldSaveCostCenterSelection
    ) {
      if (shouldSaveCostCenterSelection) {
        saveSelection(selectedOptions.map((o) => o.uniqId));
      } else {
        saveSelection([]);
      }
    }
  }, [prevShouldSaveCostCenterSelection, shouldSaveCostCenterSelection]);

  useEffect(() => {
    if (setPlanUnits) {
      setPlanUnits(options);
    }
  }, [options, setPlanUnits]);

  useEffect(() => {
    if (selectedValues) {
      const ids = selectedValues.map((o) => o.uniqId ?? o.id);
      setSelectedOptionsIds(ids); // Overwrite internal state when parent controls
    }
  }, [selectedValues]);

  return (
    <StyledPlanUnitSelectorBox
      className={classNames(className, 'planunit-selector', {
        'full-width': fullWidth
      })}
      isToolbar={isToolbar}
    >
      {isMulti ? (
        <>
          <VirtualizedAutocomplete
            autoHighlight
            disableCloseOnSelect
            clearOnBlur={false}
            clearOnEscape={false}
            extraWidth={extraWidth}
            filterOptions={filterOptions}
            getLimitTagsText={(cnt) =>
              `${cnt} ${t('selected').toLocaleLowerCase()}`
            }
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(a, b) => a.uniqId === b.uniqId}
            limitTags={showChips ? undefined : 0}
            multiple
            onChange={onChange}
            onClose={() => setOpen(false)}
            onOpenCallback={() => setOpen(true)}
            open={isOpen}
            options={options}
            size="small"
            renderInput={(params) => (
              <StyledCustomTextField
                label={t('planUnits')}
                error={error}
                helperText={helperText}
                hiddenLabel={isToolbar}
                variant={variant}
                isToolbar={isToolbar}
                onChange={(e) => {
                  setPlanUnitSearchCriteria(e.target.value);
                }}
                onBlur={() => {
                  setPlanUnitSearchCriteria('');
                }}
                {...params}
                size="small"
              />
            )}
            renderOption={renderMultiOption}
            renderTags={renderTags}
            value={selectedOptions}
            fullWidth={fullWidth}
            inputValue={inputValue || planUnitSearchCriteria}
            onInputChange={(event, value, reason) => {
              reason === 'clear' && onClear && onClear();

              if (event && event.type === 'blur') {
                setInputValue('');
                setPlanUnitSearchCriteria('');
              } else if (reason !== 'reset') {
                setInputValue(value);
              }
            }}
            disabled={disabled}
          />
        </>
      ) : (
        <VirtualizedAutocomplete
          autoHighlight
          extraWidth={extraWidth}
          filterOptions={filterOptions}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(a, b) => a.uniqId === b.uniqId}
          onChange={onChange}
          options={options}
          renderInput={renderInput}
          renderOption={renderOption}
          value={selectedOptions[0] || null}
          onInputChange={(event, value, reason) => {
            reason === 'clear' && onClear && onClear();
          }}
          fullWidth={fullWidth}
          disabled={disabled}
          size={size}
        />
      )}
      {(!hideNavigation || !hideGroups) && !disabled && (
        <div className="d-flex v-center">
          <ArrowNavigation
            onNavigate={onArrowNavigate}
            options={options}
            selectedOption={selectedOptions[0]}
            disabled={isMulti}
            showCnt={!props.hideNavigation && props.showNavigationCnt}
            isToolbar={isToolbar}
          />
          {!hideGroups && (
            <Tooltip title={<span>{t('editPlanGroups')}</span>}>
              <IconButton
                color={isToolbar ? 'inherit' : 'primary'}
                onClick={() => setShowPlanGroupsDialog(true)}
                className="edit-plangroups"
                size="small"
                disabled={disabled}
              >
                <Icon type="folder-plus" fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
      {showPlanGroupsDialog && (
        <PlanGroupsDialog onHidden={() => setShowPlanGroupsDialog(false)} />
      )}
      {isToolbar && !isVertical && (
        <StyledToolbarDivider
          orientation="vertical"
          variant="middle"
          flexItem
        />
      )}
    </StyledPlanUnitSelectorBox>
  );
}
